/**
 * アバター(docs/02_game_design.md §7.1 / §7.3)。
 *
 * ベースキャラ(絵文字)+ 6スロットの装備。装備アイテムの `effects` は
 * そのまま engine の `ScoreModifier[]` として `TypingSession` に渡され、
 * ゲージ減衰・コンボ上限・ミス見逃し・コイン獲得に実際に効く。
 *
 * アート素材ができるまで見た目は絵文字 + レアリティのCSS装飾で表す。
 */

import { resolveModifiers, type ScoreModifier, type ScoreModifierType } from '@paltyping/engine';
import { ITEM_SLOTS, type Catalog, type CatalogItem, type ItemSlot } from '../services/catalog';
import type { InventorySnapshot } from '../services/inventory';

export interface BaseCharacter {
  readonly id: string;
  readonly name: string;
  readonly emoji: string;
  /** CSS の背景に使うアクセント色。 */
  readonly color: string;
}

/** ベースキャラ(最初から全部えらべる)。 */
export const BASE_CHARACTERS: readonly BaseCharacter[] = [
  { id: 'base-01', name: 'ぱるくん', emoji: '🙂', color: '#ffd97a' },
  { id: 'base-02', name: 'ぱるちゃん', emoji: '😺', color: '#ffb3c7' },
  { id: 'base-03', name: 'ぽちすけ', emoji: '🐶', color: '#a8dda8' },
  { id: 'base-04', name: 'ぴよた', emoji: '🐤', color: '#ffe999' },
  { id: 'base-05', name: 'ろぼっち', emoji: '🤖', color: '#9fd3ff' },
];

export function baseCharacterOf(id: string): BaseCharacter {
  return BASE_CHARACTERS.find((base) => base.id === id) ?? (BASE_CHARACTERS[0] as BaseCharacter);
}

export const SLOT_LABELS: Readonly<Record<ItemSlot, string>> = {
  hat: 'ぼうし',
  face: 'かお',
  outfit: 'ふく',
  hand: 'てにもつ',
  background: 'はいけい',
  effect: 'エフェクト',
};

export const RARITY_LABELS = { N: 'N', R: 'R', SR: 'SR', UR: 'UR' } as const;

/** 装備中のアイテム(スロット順)。 */
export function equippedItems(
  catalog: Catalog,
  inventory: InventorySnapshot,
): readonly CatalogItem[] {
  const items: CatalogItem[] = [];
  for (const slot of ITEM_SLOTS) {
    const itemId = inventory.equipped[slot];
    if (itemId === undefined) continue;
    const item = catalog.itemsById.get(itemId);
    if (item) items.push(item);
  }
  return items;
}

/** 装備中のアイテムから engine に渡す効果一覧を作る。 */
export function equippedModifiers(
  catalog: Catalog,
  inventory: InventorySnapshot,
): readonly ScoreModifier[] {
  return equippedItems(catalog, inventory).flatMap((item) => item.effects);
}

/** 保有アイテムをスロットごとに分ける(アバター画面の選択肢)。 */
export function ownedBySlot(
  catalog: Catalog,
  inventory: InventorySnapshot,
): Readonly<Record<ItemSlot, readonly CatalogItem[]>> {
  const result = {} as Record<ItemSlot, CatalogItem[]>;
  for (const slot of ITEM_SLOTS) result[slot] = [];
  for (const item of catalog.items) {
    if ((inventory.items[item.id] ?? 0) > 0) result[item.slot].push(item);
  }
  return result;
}

const EFFECT_LABELS: Readonly<Record<ScoreModifierType, (value: number) => string>> = {
  gaugeDecayReduction: (value) => `ゲージが へりにくい -${Math.round(value * 100)}%`,
  comboCapUp: (value) => `コンボばいりつの じょうげん +${value}`,
  missForgive: (value) => `ミス ${Math.round(value)}かいまで コンボが きえない`,
  coinBonus: (value) => `コインが +${Math.round(value * 100)}%`,
};

/** 1つの効果を子ども向けの説明文にする。 */
export function describeEffect(modifier: ScoreModifier): string {
  return EFFECT_LABELS[modifier.type](modifier.value);
}

export interface EffectSummaryRow {
  readonly type: ScoreModifierType;
  readonly text: string;
}

/**
 * 装備の合計効果(上限クランプ後)を説明文にする。
 * engine の `resolveModifiers` を通すので、UI の表示と実際の効果が必ず一致する。
 */
export function summarizeModifiers(
  modifiers: readonly ScoreModifier[],
): readonly EffectSummaryRow[] {
  const resolved = resolveModifiers(modifiers);
  const base = resolveModifiers([]);
  const rows: EffectSummaryRow[] = [];
  if (resolved.gaugeDecayReduction > 0) {
    rows.push({
      type: 'gaugeDecayReduction',
      text: EFFECT_LABELS.gaugeDecayReduction(resolved.gaugeDecayReduction),
    });
  }
  if (resolved.comboBonusCap > base.comboBonusCap) {
    const up = Math.round((resolved.comboBonusCap - base.comboBonusCap) * 100) / 100;
    rows.push({ type: 'comboCapUp', text: EFFECT_LABELS.comboCapUp(up) });
  }
  if (resolved.missForgive > 0) {
    rows.push({ type: 'missForgive', text: EFFECT_LABELS.missForgive(resolved.missForgive) });
  }
  if (resolved.coinMultiplier > 1) {
    const up = Math.round((resolved.coinMultiplier - 1) * 100) / 100;
    rows.push({ type: 'coinBonus', text: EFFECT_LABELS.coinBonus(up) });
  }
  return rows;
}
